import type { QueryClient } from "@tanstack/react-query";
import { ReportController } from "../domain/controllers/report-controller";
import type { Chart } from "../domain/models/chart";
import { t } from "../i18n/en";
import { appQueryKeys, type WizardQueryState } from "./app-query-state";
import { getPendingFriendInvite } from "./friend-invite";
import { captureAuthEvent } from "./observability";
import "../domain/models/reports/full-substructure-table-report";
import type { IncarnationCross } from "../utils/calculations";

export interface ChartSummaryDetails {
  definition: string;
  gates: string[];
  channels: string[];
  variableTypes: string[];
}

export interface SavedChartLibraryItem {
  id: string;
  username?: string;
  email: string;
  createdAt: string;
  birthDate: string;
  birthTime: string;
  location: string;
  birthLatitude?: number;
  birthLongitude?: number;
  birthTimeZone?: string;
  phoneNumber?: string;
  chartName: string;
  energyType: string;
  authority: string;
  profile: string;
  incarnationCrossName: string;
  chart: Chart;
  incarnationCross: IncarnationCross;
  reportOutput: string;
  isOwnerChart?: boolean;
  operationalEmailConsent?: boolean;
  marketingEmailConsent?: boolean;
  emailConsentRecordedAt?: string;
  linkedFriendshipId?: string;
  profilePhoto?: string;
  summary?: ChartSummaryDetails;
}

export type ChartIdentity = Pick<
  SavedChartLibraryItem,
  "chartName" | "birthDate" | "birthTime" | "location"
>;

interface SavedChartLibrary {
  charts: SavedChartLibraryItem[];
}

export class ChartLibraryRequestError extends Error {
  constructor(
    message: string,
    public status: number,
    public requestId?: string,
  ) {
    super(message);
    this.name = "ChartLibraryRequestError";
  }
}

export interface ChartLibrarySession {
  email: string;
  token: string;
  expiresAt: string;
  impersonatedBy?: string;
}

export const VIEWER_EMAIL_STORAGE_KEY = "human-design.viewer-email";

const CHART_LIBRARY_STORAGE_KEY = "human-design.chart-library";
const VIEWER_EMAIL_HISTORY_STORAGE_KEY = "human-design.viewer-email-history";
const COMPATIBILITY_SELECTION_STORAGE_KEY = "human-design.compatibility-selection";
const COMPATIBILITY_CHARTS_STORAGE_KEY = "human-design.compatibility-charts";
const AUTH_SESSION_STORAGE_KEY = "human-design.library-session";
const ADMIN_SESSION_STORAGE_KEY = "human-design.admin-session-before-impersonation";
const GUEST_LIBRARY_EMAIL = "guest";
const BULK_SYNC_SIZE = 25;
const SAVED_CHART_CACHE_TTL_MS = 15_000;
const CHART_SUMMARY_CACHE_TTL_MS = 60_000;
const SUMMARY_PAGE_SIZE = 50;
const SUMMARY_PAGE_CONCURRENCY = 3;
const SYNC_STATUS_EVENT = "human-design:chart-library-sync";

export interface ChartLibrarySyncStatus {
  state: "idle" | "syncing" | "synced" | "error";
  total: number;
  completed: number;
  remaining: number;
  message: string;
}

let chartLibrarySyncStatus: ChartLibrarySyncStatus = {
  state: "idle",
  total: 0,
  completed: 0,
  remaining: 0,
  message: "",
};

const savedChartsCache = new Map<string, { charts: SavedChartLibraryItem[]; expiresAt: number }>();
const savedChartsRequests = new Map<string, Promise<SavedChartLibraryItem[]>>();
const chartSummariesCache = new Map<
  string,
  { charts: SavedChartLibraryItem[]; expiresAt: number }
>();
const chartSummariesRequests = new Map<string, Promise<SavedChartLibraryItem[]>>();
const chartDetailsCache = new Map<string, { chart: SavedChartLibraryItem; expiresAt: number }>();
const chartDetailsRequests = new Map<string, Promise<SavedChartLibraryItem>>();

export function invalidateSavedChartsCache(email?: string) {
  if (email) {
    const key = normalizeEmail(email);
    savedChartsCache.delete(key);
    savedChartsRequests.delete(key);
    chartSummariesCache.delete(key);
    chartSummariesRequests.delete(key);
    for (const detailKey of chartDetailsCache.keys()) {
      if (detailKey.startsWith(`${key}\u0000`)) chartDetailsCache.delete(detailKey);
    }
    for (const detailKey of chartDetailsRequests.keys()) {
      if (detailKey.startsWith(`${key}\u0000`)) chartDetailsRequests.delete(detailKey);
    }
    return;
  }
  savedChartsCache.clear();
  savedChartsRequests.clear();
  chartSummariesCache.clear();
  chartSummariesRequests.clear();
  chartDetailsCache.clear();
  chartDetailsRequests.clear();
}

function publishSyncStatus(status: ChartLibrarySyncStatus) {
  chartLibrarySyncStatus = status;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SYNC_STATUS_EVENT, { detail: status }));
  }
}

export function getChartLibrarySyncStatus() {
  return chartLibrarySyncStatus;
}

export function subscribeToChartLibrarySyncStatus(
  listener: (status: ChartLibrarySyncStatus) => void,
) {
  if (typeof window === "undefined") return () => {};
  const handleStatus = (event: Event) =>
    listener((event as CustomEvent<ChartLibrarySyncStatus>).detail);
  window.addEventListener(SYNC_STATUS_EVENT, handleStatus);
  return () => window.removeEventListener(SYNC_STATUS_EVENT, handleStatus);
}

function normalizeEmail(email: string | null | undefined) {
  return email?.trim().toLowerCase() || GUEST_LIBRARY_EMAIL;
}

function readLibrary(): SavedChartLibrary {
  if (typeof window === "undefined") {
    return { charts: [] };
  }

  try {
    const rawLibrary = window.localStorage.getItem(CHART_LIBRARY_STORAGE_KEY);
    if (!rawLibrary) {
      return { charts: [] };
    }

    const parsed = JSON.parse(rawLibrary);

    if (!Array.isArray(parsed.charts)) {
      return { charts: [] };
    }

    return { charts: parsed.charts };
  } catch {
    return { charts: [] };
  }
}

function writeLibrary(library: SavedChartLibrary) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(CHART_LIBRARY_STORAGE_KEY, JSON.stringify(library));
}

function findLikelyOwnerChart(charts: SavedChartLibraryItem[], email: string) {
  const emailName =
    normalizeEmail(email)
      .split("@")[0]
      ?.replace(/[^a-z0-9]/g, "") || "";
  return charts.reduce<{ chart: SavedChartLibraryItem; score: number } | null>((best, chart) => {
    const chartName = chart.chartName.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (chartName.length < 2 || !emailName.includes(chartName)) {
      return best;
    }
    const score = (emailName.startsWith(chartName) ? 100 : 0) + chartName.length;
    return !best || score > best.score ? { chart, score } : best;
  }, null)?.chart;
}

/**
 * Older accounts can have a perfectly valid one-chart library without the
 * owner flag that newer saves add. Treat that unambiguous chart as the owner
 * everywhere instead of sending a returning person through onboarding again.
 */
export function findOwnerChart(charts: SavedChartLibraryItem[], email: string) {
  return (
    charts.find((chart) => chart.isOwnerChart) ||
    findLikelyOwnerChart(charts, email) ||
    (charts.length === 1 ? charts[0] : undefined)
  );
}

function ensureLocalOwnerChart(email: string) {
  const library = readLibrary();
  const accountCharts = library.charts.filter((chart) => chart.email === normalizeEmail(email));
  if (accountCharts.length === 0) {
    return;
  }

  const ownerChart = findOwnerChart(accountCharts, email);
  const markedOwner = accountCharts.find((chart) => chart.isOwnerChart);
  if (!ownerChart || markedOwner?.id === ownerChart.id) {
    return;
  }

  for (const chart of accountCharts) {
    chart.isOwnerChart = chart.id === ownerChart.id;
  }
  writeLibrary(library);
}

export function setViewerEmail(email: string) {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedEmail = normalizeEmail(email);
  window.localStorage.setItem(VIEWER_EMAIL_STORAGE_KEY, normalizedEmail);

  if (normalizedEmail !== GUEST_LIBRARY_EMAIL) {
    const history = getKnownViewerEmails().filter((item) => item !== normalizedEmail);
    window.localStorage.setItem(
      VIEWER_EMAIL_HISTORY_STORAGE_KEY,
      JSON.stringify([normalizedEmail, ...history].slice(0, 5)),
    );
  }
}

export function getViewerEmail() {
  if (typeof window === "undefined") {
    return GUEST_LIBRARY_EMAIL;
  }

  return normalizeEmail(window.localStorage.getItem(VIEWER_EMAIL_STORAGE_KEY));
}

export function getKnownViewerEmails() {
  if (typeof window === "undefined") {
    return [];
  }

  const emails = new Set<string>();
  const currentEmail = normalizeEmail(window.localStorage.getItem(VIEWER_EMAIL_STORAGE_KEY));
  if (currentEmail !== GUEST_LIBRARY_EMAIL) {
    emails.add(currentEmail);
  }

  try {
    const savedHistory = JSON.parse(
      window.localStorage.getItem(VIEWER_EMAIL_HISTORY_STORAGE_KEY) || "[]",
    );
    if (Array.isArray(savedHistory)) {
      for (const email of savedHistory) {
        const normalizedEmail = normalizeEmail(typeof email === "string" ? email : null);
        if (normalizedEmail !== GUEST_LIBRARY_EMAIL) {
          emails.add(normalizedEmail);
        }
      }
    }
  } catch {
    window.localStorage.removeItem(VIEWER_EMAIL_HISTORY_STORAGE_KEY);
  }

  for (const chart of readLibrary().charts) {
    const normalizedEmail = normalizeEmail(chart.email);
    if (normalizedEmail !== GUEST_LIBRARY_EMAIL) {
      emails.add(normalizedEmail);
    }
  }

  return [...emails].slice(0, 5);
}

export function getChartLibrarySession() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawSession = window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
    if (!rawSession) {
      return null;
    }

    const session = JSON.parse(rawSession) as ChartLibrarySession;
    if (!session.email || !session.token || new Date(session.expiresAt).getTime() <= Date.now()) {
      window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
      return null;
    }

    return session;
  } catch {
    window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    return null;
  }
}

export function setChartLibrarySession(session: ChartLibrarySession) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session));
  setViewerEmail(session.email);
  invalidateSavedChartsCache(session.email);
}

export function clearChartLibrarySession() {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
  invalidateSavedChartsCache();
}

export function beginImpersonation(session: ChartLibrarySession) {
  if (typeof window === "undefined") return;
  const current = getChartLibrarySession();
  if (current && !current.impersonatedBy) {
    window.localStorage.setItem(ADMIN_SESSION_STORAGE_KEY, JSON.stringify(current));
  }
  setChartLibrarySession(session);
}

export function endImpersonation() {
  if (typeof window === "undefined") return false;
  try {
    const rawSession = window.localStorage.getItem(ADMIN_SESSION_STORAGE_KEY);
    if (!rawSession) return false;
    const session = JSON.parse(rawSession) as ChartLibrarySession;
    window.localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
    if (!session.email || !session.token || new Date(session.expiresAt).getTime() <= Date.now()) {
      return false;
    }
    setChartLibrarySession(session);
    return true;
  } catch {
    window.localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
    return false;
  }
}

async function apiRequest<T>(path: string, options: RequestInit = {}) {
  const session = getChartLibrarySession();
  const headers = new Headers(options.headers);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (session && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${session.token}`);
  }

  const response = await fetch(path, {
    ...options,
    headers,
  });
  const requestId = response.headers.get("X-Request-Id") || undefined;
  const rawBody = await response.text();
  let data: (T & { error?: string }) | null = null;

  if (rawBody) {
    try {
      data = JSON.parse(rawBody) as T & { error?: string };
    } catch {
      throw new ChartLibraryRequestError(
        `The server returned an unexpected response for ${path}.${requestId ? ` Request ID: ${requestId}.` : ""}`,
        response.status,
        requestId,
      );
    }
  }

  if (!response.ok) {
    if (response.status === 401) {
      clearChartLibrarySession();
    }
    throw new ChartLibraryRequestError(
      `${data?.error || `Request failed (${response.status}).`}${requestId ? ` Request ID: ${requestId}.` : ""}`,
      response.status,
      requestId,
    );
  }

  if (!data) {
    throw new ChartLibraryRequestError(
      `The server returned an empty response for ${path}.${requestId ? ` Request ID: ${requestId}.` : ""}`,
      response.status,
      requestId,
    );
  }

  return data;
}

export async function requestLibrarySignIn(email: string, invite = "") {
  captureAuthEvent("auth_sign_in_requested", { method: "email", invitePresent: Boolean(invite) });
  try {
    return await apiRequest<{ sent: boolean; devMagicLink?: string }>("/api/auth-start", {
      method: "POST",
      body: JSON.stringify({ email: normalizeEmail(email), invite }),
    });
  } catch (error) {
    captureAuthEvent("auth_sign_in_failed", { method: "email", invitePresent: Boolean(invite) });
    throw error;
  }
}

export async function completeLibrarySignIn(token: string) {
  try {
    const session = await apiRequest<ChartLibrarySession>("/api/auth-verify", {
      method: "POST",
      body: JSON.stringify({ token }),
    });

    setChartLibrarySession(session);
    await migrateLocalChartsToAuthenticatedLibrary(session.email);
    captureAuthEvent("auth_sign_in_completed", {
      method: "email",
      invitePresent: Boolean(getPendingFriendInvite()),
    });
    return session;
  } catch (error) {
    captureAuthEvent("auth_sign_in_failed", {
      method: "email",
      invitePresent: Boolean(getPendingFriendInvite()),
    });
    throw error;
  }
}

export async function completeGoogleSignIn(credential: string) {
  try {
    const session = await apiRequest<ChartLibrarySession>("/api/google-auth", {
      method: "POST",
      body: JSON.stringify({ credential }),
    });

    setChartLibrarySession(session);
    await migrateLocalChartsToAuthenticatedLibrary(session.email);
    captureAuthEvent("auth_sign_in_completed", {
      method: "google",
      invitePresent: Boolean(getPendingFriendInvite()),
    });
    return session;
  } catch (error) {
    captureAuthEvent("auth_sign_in_failed", {
      method: "google",
      invitePresent: Boolean(getPendingFriendInvite()),
    });
    throw error;
  }
}

export async function completeGoogleRedirectSignIn() {
  try {
    const session = await apiRequest<ChartLibrarySession>("/api/google-auth", {
      method: "POST",
      body: JSON.stringify({}),
    });

    setChartLibrarySession(session);
    await migrateLocalChartsToAuthenticatedLibrary(session.email);
    captureAuthEvent("auth_sign_in_completed", {
      method: "google",
      invitePresent: Boolean(getPendingFriendInvite()),
    });
    return session;
  } catch (error) {
    captureAuthEvent("auth_sign_in_failed", {
      method: "google",
      invitePresent: Boolean(getPendingFriendInvite()),
    });
    throw error;
  }
}

function getLocalSavedChartsForEmail(email = getViewerEmail()) {
  const normalizedEmail = normalizeEmail(email);
  ensureLocalOwnerChart(normalizedEmail);

  return readLibrary()
    .charts.filter((item) => item.email === normalizedEmail)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function getRecoverableDeviceCharts(email = getChartLibrarySession()?.email) {
  const normalizedEmail = normalizeEmail(email);

  if (normalizedEmail === GUEST_LIBRARY_EMAIL) {
    return [];
  }

  return readLibrary().charts.filter((chart) => normalizeEmail(chart.email) !== normalizedEmail);
}

function looksLikeSavedChart(value: unknown): value is SavedChartLibraryItem {
  if (!value || typeof value !== "object") return false;
  const chart = value as Partial<SavedChartLibraryItem>;
  return (
    typeof chart.chartName === "string" &&
    typeof chart.birthDate === "string" &&
    typeof chart.birthTime === "string" &&
    typeof chart.location === "string" &&
    Boolean(chart.chart && typeof chart.chart === "object") &&
    Boolean(chart.incarnationCross && typeof chart.incarnationCross === "object")
  );
}

export function scanBrowserStorageForCharts() {
  if (typeof window === "undefined") return [];

  const chartsByIdentity = new Map<string, SavedChartLibraryItem>();
  const scanStorage = (storage: Storage) => {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key) continue;

      try {
        const root: unknown = JSON.parse(storage.getItem(key) || "null");
        const values: unknown[] = [root];
        const visited = new Set<object>();
        let examined = 0;

        while (values.length > 0 && examined < 10_000) {
          const value = values.pop();
          examined += 1;
          if (!value || typeof value !== "object" || visited.has(value)) continue;
          visited.add(value);

          if (looksLikeSavedChart(value)) {
            const candidate: SavedChartLibraryItem = {
              ...value,
              id: typeof value.id === "string" ? value.id : crypto.randomUUID(),
              email: normalizeEmail(typeof value.email === "string" ? value.email : null),
              createdAt:
                typeof value.createdAt === "string" ? value.createdAt : new Date().toISOString(),
              energyType: typeof value.energyType === "string" ? value.energyType : "",
              authority: typeof value.authority === "string" ? value.authority : "",
              profile: typeof value.profile === "string" ? value.profile : "",
              incarnationCrossName:
                typeof value.incarnationCrossName === "string" ? value.incarnationCrossName : "",
              reportOutput: typeof value.reportOutput === "string" ? value.reportOutput : "",
            };
            chartsByIdentity.set(getChartIdentityKey(candidate), candidate);
          }

          if (Array.isArray(value)) values.push(...value);
          else values.push(...Object.values(value));
        }
      } catch {
        // Non-JSON browser entries cannot contain a serialized Harness chart.
      }
    }
  };

  scanStorage(window.localStorage);
  return [...chartsByIdentity.values()];
}

async function gzipJsonForRecovery(value: unknown) {
  if (typeof CompressionStream === "undefined") {
    throw new Error(t("ui.copy_this_safari_version_cannot_compress_980f1beb"));
  }

  const compressed = await new Response(
    new Blob([JSON.stringify(value)], { type: "application/json" })
      .stream()
      .pipeThrough(new CompressionStream("gzip")),
  ).arrayBuffer();
  const bytes = new Uint8Array(compressed);
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return window.btoa(binary);
}

export async function recoverScannedChartsToAuthenticatedLibrary(
  scannedCharts: SavedChartLibraryItem[],
) {
  const session = getChartLibrarySession();
  if (!session) throw new Error(t("ui.copy_sign_in_before_recovering_charts_sav_f8fb04a8"));

  const chartsToRecover = scannedCharts;
  let recovered = 0;
  let alreadyPresent = 0;

  // Use the same proven one-chart endpoint as ordinary chart creation. Keep
  // the old device record intact; this path only adds its cloud counterpart.
  for (const chart of chartsToRecover) {
    const { id: _id, email: _email, createdAt: _createdAt, ...item } = chart;
    const recoveryItem = {
      ...item,
      // Analysis output is derived entirely from the chart and can be rebuilt
      // on open. Omitting it makes the compressed recovery payload smaller.
      reportOutput: "",
      // Never replace the chart already marked as the signed-in user's own.
      isOwnerChart: false,
    };
    const result = await apiRequest<{ recovered: boolean }>("/api/chart-library", {
      method: "POST",
      body: JSON.stringify({
        recoveryPayload: await gzipJsonForRecovery(recoveryItem),
      }),
    });
    if (result.recovered) recovered += 1;
    else alreadyPresent += 1;
  }

  return { recovered, alreadyPresent };
}

export async function recoverDeviceChartsToAuthenticatedLibrary() {
  const session = getChartLibrarySession();
  if (!session) {
    throw new Error(t("ui.copy_sign_in_before_recovering_charts_sav_f8fb04a8"));
  }

  const deviceCharts = getRecoverableDeviceCharts(session.email);
  if (deviceCharts.length === 0) {
    return { recovered: 0, alreadyPresent: 0, remaining: 0 };
  }

  const remoteLibrary = await apiRequest<SavedChartLibrary>("/api/chart-library");
  const remoteIdentityKeys = new Set(remoteLibrary.charts.map(getChartIdentityKey));
  const remoteHasOwner = remoteLibrary.charts.some((chart) => chart.isOwnerChart);
  let recovered = 0;
  let alreadyPresent = 0;

  for (const deviceChart of deviceCharts) {
    if (remoteIdentityKeys.has(getChartIdentityKey(deviceChart))) {
      removeLocalSavedChart(deviceChart.id);
      alreadyPresent += 1;
    }
  }

  const chartsToRecover = deviceCharts.filter(
    (chart) => !remoteIdentityKeys.has(getChartIdentityKey(chart)),
  );

  for (let offset = 0; offset < chartsToRecover.length; offset += BULK_SYNC_SIZE) {
    const batch = chartsToRecover.slice(offset, offset + BULK_SYNC_SIZE);
    const data = await apiRequest<{
      synced: Array<{ clientId: string; item: SavedChartLibraryItem }>;
    }>("/api/chart-library", {
      method: "POST",
      body: JSON.stringify({
        items: batch.map((deviceChart) => {
          const { id, email: _email, createdAt, ...item } = deviceChart;
          return {
            clientId: id,
            createdAt,
            item: remoteHasOwner ? { ...item, isOwnerChart: false } : item,
          };
        }),
      }),
    });

    // Only remove an old device copy after the server confirms that exact item.
    for (const result of data.synced) {
      removeLocalSavedChart(result.clientId);
      recovered += 1;
      remoteIdentityKeys.add(getChartIdentityKey(result.item));
    }
  }

  return {
    recovered,
    alreadyPresent,
    remaining: getRecoverableDeviceCharts(session.email).length,
  };
}

async function migrateLocalChartsToAuthenticatedLibrary(
  email: string,
  knownRemoteCharts: SavedChartLibraryItem[] = [],
) {
  const localCharts = getLocalSavedChartsForEmail(email);
  const syncedCharts: SavedChartLibraryItem[] = [];

  const remoteIdentityKeys = new Set(knownRemoteCharts.map(getChartIdentityKey));
  const unsyncedCharts = localCharts.filter(
    (chart) => !remoteIdentityKeys.has(getChartIdentityKey(chart)),
  );

  for (const localChart of localCharts) {
    if (remoteIdentityKeys.has(getChartIdentityKey(localChart))) {
      removeLocalSavedChart(localChart.id);
    }
  }

  if (unsyncedCharts.length === 0) {
    publishSyncStatus({ state: "synced", total: 0, completed: 0, remaining: 0, message: "Saved" });
    return syncedCharts;
  }

  publishSyncStatus({
    state: "syncing",
    total: unsyncedCharts.length,
    completed: 0,
    remaining: unsyncedCharts.length,
    message: t("ui.copy_syncing_value_charts_7dfaee36", { count: unsyncedCharts.length }),
  });

  for (let offset = 0; offset < unsyncedCharts.length; offset += BULK_SYNC_SIZE) {
    const batch = unsyncedCharts.slice(offset, offset + BULK_SYNC_SIZE);
    try {
      const data = await apiRequest<{
        synced: Array<{ clientId: string; item: SavedChartLibraryItem }>;
      }>("/api/chart-library", {
        method: "POST",
        body: JSON.stringify({
          items: batch.map((localChart) => {
            const { id, email: _email, createdAt, ...item } = localChart;
            return { clientId: id, createdAt, item };
          }),
        }),
      });
      for (const result of data.synced) {
        syncedCharts.push(result.item);
        removeLocalSavedChart(result.clientId);
      }
      const completed = Math.min(offset + batch.length, unsyncedCharts.length);
      publishSyncStatus({
        state: completed === unsyncedCharts.length ? "synced" : "syncing",
        total: unsyncedCharts.length,
        completed,
        remaining: unsyncedCharts.length - completed,
        message:
          completed === unsyncedCharts.length
            ? `${completed} charts synced`
            : `Syncing charts… ${completed} of ${unsyncedCharts.length}`,
      });
    } catch (error) {
      console.error("Could not migrate chart batch to the private library:", error);
      publishSyncStatus({
        state: "error",
        total: unsyncedCharts.length,
        completed: offset,
        remaining: unsyncedCharts.length - offset,
        message: t("ui.copy_sync_paused_harness_will_retry_autom_0435c9e2"),
      });
      break;
    }
  }

  return syncedCharts;
}

async function loadSavedChartsForEmail(session: ChartLibrarySession) {
  const localCharts = getLocalSavedChartsForEmail(session.email);
  let remoteCharts: SavedChartLibraryItem[] = [];

  try {
    const pageSize = 50;
    const firstPage = await apiRequest<SavedChartLibrary & { total?: number }>(
      `/api/chart-library?offset=0&limit=${pageSize}`,
    );
    remoteCharts = firstPage.charts;
    const total = typeof firstPage.total === "number" ? firstPage.total : remoteCharts.length;
    const remainingOffsets = [];
    for (let offset = pageSize; offset < total; offset += pageSize) {
      remainingOffsets.push(offset);
    }
    if (remainingOffsets.length > 0) {
      const remainingPages = await Promise.all(
        remainingOffsets.map((offset) =>
          apiRequest<SavedChartLibrary & { total?: number }>(
            `/api/chart-library?offset=${offset}&limit=${pageSize}`,
          ),
        ),
      );
      remoteCharts = remoteCharts.concat(...remainingPages.map((page) => page.charts));
    }
  } catch (error) {
    console.error("Could not refresh the private library; showing device backups:", error);
  }

  const newlySyncedCharts = await migrateLocalChartsToAuthenticatedLibrary(
    session.email,
    remoteCharts,
  );

  const chartsByIdentity = new Map(
    remoteCharts.map((chart) => [getChartIdentityKey(chart), chart]),
  );
  for (const chart of localCharts) {
    chartsByIdentity.set(getChartIdentityKey(chart), chart);
  }
  for (const chart of newlySyncedCharts) {
    chartsByIdentity.set(getChartIdentityKey(chart), chart);
  }

  const visibleCharts = [...chartsByIdentity.values()].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );

  if (remoteCharts.length > 0) {
    for (const localChart of localCharts) {
      const confirmedRemoteChart = remoteCharts.some(
        (remoteChart) =>
          remoteChart.id === localChart.id || chartsHaveSameIdentity(remoteChart, localChart),
      );
      if (confirmedRemoteChart) {
        removeLocalSavedChart(localChart.id);
      }
    }
  }

  return visibleCharts;
}

export async function getSavedChartsForEmail(email = getViewerEmail()) {
  const session = getChartLibrarySession();

  if (!session) return getLocalSavedChartsForEmail(email);

  const key = normalizeEmail(session.email);
  const cached = savedChartsCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.charts;

  const existingRequest = savedChartsRequests.get(key);
  if (existingRequest) return existingRequest;

  let request: Promise<SavedChartLibraryItem[]>;
  request = loadSavedChartsForEmail(session)
    .then((charts) => {
      if (savedChartsRequests.get(key) === request) {
        savedChartsCache.set(key, { charts, expiresAt: Date.now() + SAVED_CHART_CACHE_TTL_MS });
      }
      return charts;
    })
    .finally(() => {
      if (savedChartsRequests.get(key) === request) savedChartsRequests.delete(key);
    });
  savedChartsRequests.set(key, request);
  return request;
}

/**
 * Fetch only the metadata needed by large library pickers. Detail payloads are
 * loaded separately when a user opens or selects a chart.
 */
export async function getSavedChartSummariesForEmail(email = getViewerEmail()) {
  const session = getChartLibrarySession();
  if (!session) return getLocalSavedChartsForEmail(email);

  const key = normalizeEmail(session.email);
  const cached = chartSummariesCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.charts;
  const existingRequest = chartSummariesRequests.get(key);
  if (existingRequest) return existingRequest;

  let request: Promise<SavedChartLibraryItem[]>;
  request = loadSavedChartSummariesForEmail(email, session.email)
    .then((charts) => {
      if (chartSummariesRequests.get(key) === request) {
        chartSummariesCache.set(key, {
          charts,
          expiresAt: Date.now() + CHART_SUMMARY_CACHE_TTL_MS,
        });
      }
      return charts;
    })
    .finally(() => {
      if (chartSummariesRequests.get(key) === request) chartSummariesRequests.delete(key);
    });
  chartSummariesRequests.set(key, request);
  return request;
}

async function loadSavedChartSummariesForEmail(email: string, sessionEmail: string) {
  const localCharts = getLocalSavedChartsForEmail(email);
  const firstPage = await apiRequest<SavedChartLibrary & { total: number }>(
    `/api/chart-library?view=summary&offset=0&limit=${SUMMARY_PAGE_SIZE}`,
  );
  const remainingOffsets = [];
  for (let offset = SUMMARY_PAGE_SIZE; offset < firstPage.total; offset += SUMMARY_PAGE_SIZE) {
    remainingOffsets.push(offset);
  }
  const remainingPages: Array<SavedChartLibrary & { total: number }> = [];
  for (let index = 0; index < remainingOffsets.length; index += SUMMARY_PAGE_CONCURRENCY) {
    const batch = remainingOffsets.slice(index, index + SUMMARY_PAGE_CONCURRENCY);
    remainingPages.push(
      ...(await Promise.all(
        batch.map((offset) =>
          apiRequest<SavedChartLibrary & { total: number }>(
            `/api/chart-library?view=summary&offset=${offset}&limit=${SUMMARY_PAGE_SIZE}`,
          ),
        ),
      )),
    );
  }
  const chartsByIdentity = new Map(
    [firstPage, ...remainingPages].flatMap((page) =>
      page.charts.map((chart) => [getChartIdentityKey(chart), chart] as const),
    ),
  );
  for (const chart of localCharts) {
    chartsByIdentity.set(getChartIdentityKey(chart), chart);
  }
  if (normalizeEmail(getChartLibrarySession()?.email) !== normalizeEmail(sessionEmail)) {
    throw new Error(t("ui.copy_your_session_changed_while_loading_c_5d1e7bdc"));
  }
  return [...chartsByIdentity.values()].sort((first, second) =>
    second.createdAt.localeCompare(first.createdAt),
  );
}

export async function getSavedChartDetails(chartId: string) {
  const session = getChartLibrarySession();
  if (!session) throw new Error(t("ui.copy_sign_in_to_load_chart_details_8121caf0"));

  const localChart = getLocalSavedChartsForEmail(session.email).find(
    (chart) => chart.id === chartId,
  );
  if (localChart) return localChart;

  const key = `${normalizeEmail(session.email)}\u0000${chartId}`;
  const cached = chartDetailsCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.chart;

  const existingRequest = chartDetailsRequests.get(key);
  if (existingRequest) return existingRequest;

  const request = apiRequest<{ chart: SavedChartLibraryItem }>(
    `/api/chart-library?id=${encodeURIComponent(chartId)}`,
  )
    .then(({ chart }) => {
      chartDetailsCache.set(key, { chart, expiresAt: Date.now() + SAVED_CHART_CACHE_TTL_MS });
      return chart;
    })
    .finally(() => chartDetailsRequests.delete(key));

  chartDetailsRequests.set(key, request);
  return request;
}

export async function getPrimaryChartForEmail(email = getViewerEmail()) {
  const session = getChartLibrarySession();
  if (!session) return findOwnerChart(getLocalSavedChartsForEmail(email), email) || null;
  if (normalizeEmail(session.email) !== normalizeEmail(email)) {
    throw new Error(t("ui.copy_you_can_only_load_the_signed_in_acco_679251d6"));
  }
  const { chart } = await apiRequest<{ chart: SavedChartLibraryItem | null }>(
    "/api/chart-library?view=owner",
  );
  if (chart) {
    const key = `${normalizeEmail(session.email)}\u0000${chart.id}`;
    chartDetailsCache.set(key, { chart, expiresAt: Date.now() + SAVED_CHART_CACHE_TTL_MS });
  }
  return chart;
}

function saveChartToLocalLibrary(
  item: Omit<SavedChartLibraryItem, "id" | "email" | "createdAt">,
  email = getViewerEmail(),
  existingChartId?: string,
  previousIdentity?: ChartIdentity,
) {
  const library = readLibrary();
  const normalizedEmail = normalizeEmail(email);
  const existingIdIndex = existingChartId
    ? library.charts.findIndex(
        (savedItem) => savedItem.email === normalizedEmail && savedItem.id === existingChartId,
      )
    : -1;
  const previousIdentityIndex =
    existingIdIndex < 0 && previousIdentity
      ? library.charts.findIndex(
          (savedItem) =>
            savedItem.email === normalizedEmail &&
            chartsHaveSameIdentity(savedItem, previousIdentity),
        )
      : -1;
  const existingIndex =
    existingIdIndex >= 0
      ? existingIdIndex
      : previousIdentityIndex >= 0
        ? previousIdentityIndex
        : library.charts.findIndex(
            (savedItem) =>
              savedItem.email === normalizedEmail &&
              savedItem.chartName.trim().toLowerCase() === item.chartName.trim().toLowerCase() &&
              savedItem.birthDate === item.birthDate &&
              savedItem.birthTime === item.birthTime &&
              savedItem.location === item.location,
          );

  const savedItem: SavedChartLibraryItem = {
    ...item,
    id:
      existingIndex >= 0
        ? library.charts[existingIndex].id
        : existingChartId || crypto.randomUUID(),
    email: normalizedEmail,
    createdAt: new Date().toISOString(),
  };

  if (savedItem.isOwnerChart) {
    for (const chart of library.charts) {
      if (chart.email === normalizedEmail) {
        chart.isOwnerChart = false;
      }
    }
  }

  if (existingIndex >= 0) {
    library.charts[existingIndex] = savedItem;
  } else {
    library.charts.push(savedItem);
  }

  writeLibrary(library);

  return savedItem;
}

export async function saveChartToLibrary(
  item: Omit<SavedChartLibraryItem, "id" | "email" | "createdAt">,
  email = getViewerEmail(),
  existingChartId?: string,
  previousIdentity?: ChartIdentity,
) {
  invalidateSavedChartsCache(email);
  // Always write a browser backup first. This makes chart creation durable even
  // when a session expires or the network drops during the server request.
  let localItem: SavedChartLibraryItem | null = null;
  let localSaveError: unknown = null;

  try {
    localItem = saveChartToLocalLibrary(item, email, existingChartId, previousIdentity);
  } catch (error) {
    localSaveError = error;
    console.error("Could not create an on-device chart backup:", error);
  }

  if (getChartLibrarySession()) {
    try {
      const data = await apiRequest<{ item: SavedChartLibraryItem }>("/api/chart-library", {
        method: "POST",
        body: JSON.stringify({ item, existingChartId, previousIdentity }),
      });
      if (localItem) {
        replaceLocalChartBackup(localItem, data.item);
      }
      // Keep the on-device backup until the next library load confirms the
      // chart. That load merges the synced response into the visible list, so
      // a cloud read that briefly lags behind its write cannot hide the chart.
      return data.item;
    } catch (error) {
      console.error("Could not sync chart to the private library; keeping local backup:", error);

      if (!localItem) {
        throw error;
      }
    }
  }

  if (localItem) {
    return localItem;
  }

  throw localSaveError instanceof Error
    ? localSaveError
    : new Error(t("ui.copy_this_chart_could_not_be_saved_please_cd4b10ef"));
}

function removeLocalSavedChart(chartId: string) {
  const library = readLibrary();
  writeLibrary({
    charts: library.charts.filter((item) => item.id !== chartId),
  });
}

export function discardLocalChartBackup(chartId: string) {
  removeLocalSavedChart(chartId);
}

function chartsHaveSameIdentity(
  first: Pick<SavedChartLibraryItem, "chartName" | "birthDate" | "birthTime" | "location">,
  second: Pick<SavedChartLibraryItem, "chartName" | "birthDate" | "birthTime" | "location">,
) {
  return (
    first.chartName.trim().toLowerCase() === second.chartName.trim().toLowerCase() &&
    first.birthDate === second.birthDate &&
    first.birthTime === second.birthTime &&
    first.location === second.location
  );
}

function getChartIdentityKey(
  chart: Pick<SavedChartLibraryItem, "chartName" | "birthDate" | "birthTime" | "location">,
) {
  return [
    chart.chartName.trim().toLowerCase(),
    chart.birthDate,
    chart.birthTime,
    chart.location,
  ].join("\u0000");
}

function replaceLocalChartBackup(
  provisionalChart: SavedChartLibraryItem,
  syncedChart: SavedChartLibraryItem,
) {
  const library = readLibrary();
  writeLibrary({
    charts: [
      ...library.charts.filter(
        (item) =>
          item.id !== provisionalChart.id &&
          item.id !== syncedChart.id &&
          !chartsHaveSameIdentity(item, syncedChart),
      ),
      syncedChart,
    ],
  });
}

function removeMatchingLocalChart(
  chartId: string,
  chart?: Pick<SavedChartLibraryItem, "chartName" | "birthDate" | "birthTime" | "location">,
) {
  const library = readLibrary();
  writeLibrary({
    charts: library.charts.filter(
      (item) => item.id !== chartId && (!chart || !chartsHaveSameIdentity(item, chart)),
    ),
  });
}

export async function removeSavedChart(
  chartId: string,
  chart?: Pick<SavedChartLibraryItem, "chartName" | "birthDate" | "birthTime" | "location">,
) {
  invalidateSavedChartsCache();
  // Remove every matching device backup first so it cannot be migrated back
  // into the cloud library while the deletion request is in flight.
  removeMatchingLocalChart(chartId, chart);

  if (getChartLibrarySession()) {
    await apiRequest<{ ok: true }>(`/api/chart-library?id=${encodeURIComponent(chartId)}`, {
      method: "DELETE",
    });
  }
}

export function restoreSavedChart(item: SavedChartLibraryItem, queryClient: QueryClient) {
  const state: WizardQueryState = {
    savedChartId: item.id,
    sourceChart: item,
    chart: item.chart,
    incarnationCross: item.incarnationCross,
    reportOutput: item.reportOutput || ReportController.logAllReports(item.chart),
    isOwnerChart: Boolean(item.isOwnerChart),
  };
  queryClient.setQueryData(appQueryKeys.wizard(), state);
}

export function setCompatibilityCharts(items: SavedChartLibraryItem[]) {
  if (typeof window === "undefined") {
    return [];
  }

  const selectedCharts = [...new Map(items.map((item) => [item.id, item])).values()].slice(0, 2);
  const selection = selectedCharts.map((item) => item.id);

  window.localStorage.setItem(COMPATIBILITY_SELECTION_STORAGE_KEY, JSON.stringify(selection));
  window.localStorage.setItem(
    COMPATIBILITY_CHARTS_STORAGE_KEY,
    JSON.stringify(Object.fromEntries(selectedCharts.map((item) => [item.id, item]))),
  );

  return selection;
}

export function getCompatibilitySelection() {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const rawSelection = window.localStorage.getItem(COMPATIBILITY_SELECTION_STORAGE_KEY);
    if (!rawSelection) {
      return [];
    }

    const parsed = JSON.parse(rawSelection);

    return Array.isArray(parsed) ? parsed.filter((chartId) => typeof chartId === "string") : [];
  } catch {
    return [];
  }
}

export function getCompatibilitySelectedCharts() {
  const selection = getCompatibilitySelection();

  try {
    const savedCharts = window.localStorage.getItem(COMPATIBILITY_CHARTS_STORAGE_KEY);
    const parsed = savedCharts ? JSON.parse(savedCharts) : {};

    return selection
      .map((chartId) => parsed[chartId])
      .filter((item: unknown): item is SavedChartLibraryItem => Boolean(item));
  } catch {
    const library = readLibrary();

    return selection
      .map((chartId) => library.charts.find((item) => item.id === chartId))
      .filter((item): item is SavedChartLibraryItem => Boolean(item));
  }
}

export function chooseChartForCompatibility(item: SavedChartLibraryItem) {
  if (typeof window === "undefined") {
    return [];
  }

  const chartId = item.id;
  const nextSelection = [
    chartId,
    ...getCompatibilitySelection().filter((id) => id !== chartId),
  ].slice(0, 2);
  const selectedCharts = Object.fromEntries(
    [item, ...getCompatibilitySelectedCharts().filter((savedItem) => savedItem.id !== chartId)]
      .slice(0, 2)
      .map((savedItem) => [savedItem.id, savedItem]),
  );

  window.localStorage.setItem(COMPATIBILITY_SELECTION_STORAGE_KEY, JSON.stringify(nextSelection));
  window.localStorage.setItem(COMPATIBILITY_CHARTS_STORAGE_KEY, JSON.stringify(selectedCharts));

  return nextSelection;
}

export function removeChartFromCompatibility(chartId: string) {
  if (typeof window === "undefined") {
    return [];
  }

  const nextSelection = getCompatibilitySelection().filter((id) => id !== chartId);
  window.localStorage.setItem(COMPATIBILITY_SELECTION_STORAGE_KEY, JSON.stringify(nextSelection));

  try {
    const savedCharts = window.localStorage.getItem(COMPATIBILITY_CHARTS_STORAGE_KEY);
    const parsedCharts = savedCharts ? JSON.parse(savedCharts) : {};
    delete parsedCharts[chartId];
    window.localStorage.setItem(COMPATIBILITY_CHARTS_STORAGE_KEY, JSON.stringify(parsedCharts));
  } catch {
    window.localStorage.removeItem(COMPATIBILITY_CHARTS_STORAGE_KEY);
  }

  return nextSelection;
}
